/* eslint-disable no-extra-boolean-cast */
/* eslint-disable no-plusplus */
import React, { createContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// -----------------LIBRARIES IMPORTS------------------- //
import moment from 'moment';
import 'moment/locale/es';
import Swal from 'sweetalert2';

// -----------------DATA IMPORTS------------------- //

// -----------------STATIC IMPORTS------------------- //
import {
	findOneOzoner,
	getOzonerByVehicle,
	updateOzoner,
} from 'models/ozoner/controllers/ozoner.controller';
import { Ozoner } from 'models/ozoner/interfaces/ozoner.interface';
import { EvidenceDocumentType } from 'models/shared/types/documents';
import { resolveS3FileUrl } from 'models/upload/upload.controller';
import profileDefaultOzoner from 'static/images/default_profile_ozoner.png';
import '../ozoner.scss';
// import { displayQuota } from './DetailsOzoner';
import { getUser } from 'localStorage/authStorage';
import { getCirculoCreditoValidation } from 'models/circulocredito/controllers/circulocredito.controller';
import { CirculoCreditoValidation } from 'models/circulocredito/types/circulocredito.type';
import {
	findAllFinancialForms,
	findAllFinancialFormsOzoner,
} from 'models/financial-form/controllers/financial-form.controller';
import { FormStatus } from 'models/financial-form/enums/financial-form.enums';
import { GenericFinancialForm } from 'models/financial-form/interfaces/financial-form.interfaces';
import { getVehicleByInternalId } from 'models/inventory/controllers/vehicle.controller';
import { VehiclePopulated } from 'models/inventory/interfaces/vehicle.interface';
import { findLoanByOzoner } from 'models/loan/controllers/loan.controller';
import { LoanWithExtraFields } from 'models/loan/interfaces/loan.interface';
import { getPaymentLinks } from 'models/openpay/controllers/openpay.controller';
import { PaymentLink } from 'models/openpay/interfaces/openpay.interface';
import { findAllOzonios } from 'models/ozonio/controllers/ozonios.controller';
import { Roles } from 'models/ozonio/enums/ozonio.enum';
import { Ozonio } from 'models/ozonio/interfaces/ozonio.interface';
import {
	getReferralCode,
	getReferrals,
} from 'models/referral-code/controllers/referral-code.controller';
import { ReferralsResponse } from 'models/referral-code/interfaces/referral-code.interface';
import { findRepossessionByLoan } from 'models/repossession/controllers/repossession.controller';
import { RepossessionStatus } from 'models/repossession/enums/repossession.enums';
import { Repossession } from 'models/repossession/interfaces/repossession.interface';
import { toasterOptions } from 'models/shared/enums/toaster.enum';
import { commonErrorHandler } from 'models/shared/helpers/errors';
import { getVehicleCurrentPrice } from 'models/shared/helpers/getVehicleCurrentPrice';
import { toasterNotification } from 'models/shared/helpers/toasterNotification';
import { SkuVariant } from 'models/sku-variant/interfaces/sku-variant.interface';
import { getSkuById } from 'models/sku/controllers/sku.controller';
import { Sku } from 'models/sku/interfaces/sku.interface';
import { getDocument } from 'models/zapsign/controllers/zapsign.controller';
import { ZapsignDocument } from 'models/zapsign/interfaces/zapsign.interface';

export interface AvailableForm {
	loan?: LoanWithExtraFields;
	form: GenericFinancialForm;
	vehicle?: VehiclePopulated;
	vehiclePrice: number;
	vehicleAvailable: boolean;
	repossession?: Repossession;
	circuloCreditoValidation?: CirculoCreditoValidation;
	referredBy?: GenericFinancialForm;
	sku?: Sku;
	skuVariant?: SkuVariant;
}
interface OzonerContextModel {
	id?: string;
	tab: number;
	setTab: React.Dispatch<React.SetStateAction<number>>;
	initializeContext: () => void;
	setId: React.Dispatch<React.SetStateAction<string | undefined>>;
	loading: boolean;
	setLoading: React.Dispatch<React.SetStateAction<boolean>>;
	ozoner?: Ozoner;
	setOzoner: React.Dispatch<React.SetStateAction<Ozoner | undefined>>;
	financialForms: AvailableForm[];
	vehicle?: VehiclePopulated;
	loans: LoanWithExtraFields[];
	selfie?: string;
	indexForm: number;
	formId?: string;
	setFormId: React.Dispatch<React.SetStateAction<string | undefined>>;
	financialForm: AvailableForm;
	canValidateCurp: boolean;
	validCurp: boolean;
	paymentLinks: PaymentLink[];
	paymentLinksLoading: boolean;
	setPaymentLinksLoading: React.Dispatch<React.SetStateAction<boolean>>;
	updatePaymentLinks: () => void;
	updateComment: (comment: string) => void;
	updateFormData: (form: GenericFinancialForm) => void;
	updateLoanData: (loan: LoanWithExtraFields) => void;
	handleDetailsVehicle: (e: any, internalId: string) => void;
	ozonios: { [key: string]: Ozonio };
	ozoniosList: { value: string; label: string }[];
	sellerOptions: { value: string; label: string }[];
	collectionManagerOptions: { value: string; label: string }[];
	openVehicle: boolean;
	setOpenVehicle: React.Dispatch<React.SetStateAction<boolean>>;
	hasLoan: boolean;
	zapsignDocuments: { [key: string]: ZapsignDocument };
	updateZapsignDocuments: (notify: boolean) => void;
	zapsignDocumentsLoading: boolean;
	referrals: ReferralsResponse[];
}

export const ozonerContext = createContext<OzonerContextModel>({
	loading: false,
} as any);

export const OzonerContextProvider = ({ children }: { children: any }) => {
	const navigate = useNavigate();

	// -----------------LOGIC STATES------------------- //
	const [loading, setLoading] = useState<boolean>(false);
	const [id, setId] = useState<string | undefined>();
	const [tab, setTab] = useState<number>(0);

	// -----------------INITIAL STATES------------------- //
	const [ozoner, setOzoner] = useState<Ozoner>();
	const [financialForms, setFinancialForms] = useState<AvailableForm[]>([]);
	const [loans, setLoans] = useState<LoanWithExtraFields[]>([]);
	const [indexForm, setIndexForm] = useState<number>(0);
	const [formId, setFormId] = useState<string>();

	const [openVehicle, setOpenVehicle] = useState<boolean>(false);

	// -----------------DOCUMENTS STATES------------------- //
	const [paymentLinks, setPaymentLinks] = useState([]);
	const [paymentLinksLoading, setPaymentLinksLoading] = useState(false);

	const [selfie, setSelfie] = useState<string>(profileDefaultOzoner);
	const [hasLoan, sethasLoan] = useState(false);

	const updateSelfieFromDocuments = (documents?: Ozoner['documents']) => {
		const selfieDoc = documents?.find(
			(doc) => doc.typeEvidence === EvidenceDocumentType.selfie,
		);

		if (!selfieDoc) {
			setSelfie(profileDefaultOzoner);
			return;
		}

		resolveS3FileUrl({ url: selfieDoc.url, path: selfieDoc.path }).then((url) =>
			setSelfie(url || profileDefaultOzoner),
		);
	};

	const [ozonios, setOzonios] = useState<{ [key: string]: Ozonio }>({});
	const [ozoniosList, setOzoniosList] = useState<
		{ value: string; label: string }[]
	>([]);
	const [sellerOptions, setSellerOptions] = useState<
		{ value: string; label: string }[]
	>([]);
	const [collectionManagerOptions, setCollectionManagerOptions] = useState<
		{ value: string; label: string }[]
	>([]);
	// -----------------REFERRAL STATES------------------- //
	const [referrals, setReferrals] = useState<ReferralsResponse[]>([]);

	const [zapsignDocuments, setZapsignDocuments] = useState<{
		[key: string]: ZapsignDocument;
	}>({});
	const [zapsignDocumentsLoading, setZapsignDocumentsLoading] = useState(false);

	const initializeContext = () => {
		if (!id) return;
		let ozonerData: Ozoner | undefined;
		let financialFormsData: GenericFinancialForm[] = [];
		let loansData: LoanWithExtraFields[] = [];
		const repossessionsData: { [key: string]: Repossession[] } = {};
		const promises = [];

		getReferrals(id).then((data) => {
			setReferrals(data);
		});

		promises.push(
			findOneOzoner(id).then((response) => {
				ozonerData = response;
				setOzoner(ozonerData);
				updateSelfieFromDocuments(ozonerData?.documents);
			}),
		);

		promises.push(
			findAllFinancialFormsOzoner(id).then((response) => {
				financialFormsData = response;
				updateSelfieFromDocuments(ozonerData?.documents);
			}),
		);
		promises.push(
			findLoanByOzoner(id).then((response) => {
				loansData = response;
				const repossessionsPromises: Promise<void | Repossession[]>[] = [];
				setLoans(loansData);
				loansData.forEach((loan) => {
					repossessionsPromises.push(
						findRepossessionByLoan(loan._id).then((repos) => {
							repossessionsData[loan._id] = repos.filter((repossession) =>
								[
									RepossessionStatus.pending,
									RepossessionStatus.completed,
								].includes(repossession.status),
							);
						}),
					);
				});
				if (response?.length) {
					sethasLoan(true);
				} else {
					sethasLoan(false);
				}
				return Promise.all(repossessionsPromises);
			}),
		);

		Promise.all(promises)
			.then(async () => {
				const availableForms: AvailableForm[] = [];
				let firstRejected = false;

				await Promise.all(
					(Array.isArray(financialFormsData) ? financialFormsData : [])
						.sort((a, b) => {
							return moment
								.utc(b.createdAt)
								.diff(moment.utc(a.createdAt), 'days');
						})
						.map(async (form) => {
							if ([FormStatus.expired].includes(form.status)) {
								if (firstRejected) return;
								firstRejected = true;
							}
							const loan = loansData.find(
								(loanData) => form._id === loanData.financialForm._id,
							);
							const circuloCreditoInForm = form.loanApplication?.circuloCredito;
							let circuloCreditoValidation:
								| CirculoCreditoValidation
								| undefined;
							if (circuloCreditoInForm) {
								const circuloCreditoId =
									typeof circuloCreditoInForm === 'string'
										? circuloCreditoInForm
										: circuloCreditoInForm._id;
								circuloCreditoValidation = await getCirculoCreditoValidation(
									circuloCreditoId,
								);
							}
							const vehicleId =
								loan?.vehicle?.internalId || form.vehicle?.internalId;
							const duration =
								loan?.duration || form.loanApplication?.loanDuration;
							let currentVehicle;
							let currentPrice;
							let vehicleAvailable = true;
							if (vehicleId) {
								currentVehicle = await getVehicleByInternalId(vehicleId);
								if (duration)
									currentPrice = getVehicleCurrentPrice(
										currentVehicle,
										duration,
									);
								if (!loan)
									await getOzonerByVehicle(form.vehicle?._id ?? '').then(
										(res) => {
											if (res && res._id !== id) vehicleAvailable = false;
										},
									);
							}
							const formSkuId = (form as any).sku as string | undefined;
							const formSkuVariantId = (form as any).skuVariant as
								| string
								| undefined;
							let currentSku: Sku | undefined;
							let currentSkuVariant: SkuVariant | undefined;
							if (formSkuId) {
								currentSku = await getSkuById(formSkuId).catch(() => undefined);
								currentSkuVariant = currentSku?.variants?.find(
									(variant) => variant._id === formSkuVariantId,
								);
							}
							let referredBy;
							if (form.loanApplication?.referredByCode) {
								referredBy =
									(await getReferralCode(form.loanApplication?.referredByCode)
										.then((code) =>
											findAllFinancialForms({ query: code.email }).then(
												(ozoners) => {
													return ozoners.data[0];
												},
											),
										)
										.catch(() => {
											toasterNotification({
												msg: 'Hubo un error al cargar la informacion de quien refiere',
												toasterType: toasterOptions.error,
												style: 'light',
											});
										})) ?? undefined;
							}

							availableForms.push({
								loan,
								form,
								vehicle: currentVehicle,
								vehiclePrice: currentPrice ?? 0,
								vehicleAvailable,
								sku: currentSku,
								skuVariant: currentSkuVariant,
								circuloCreditoValidation,
								repossession:
									loan && repossessionsData[loan._id]?.length
										? repossessionsData[loan._id][0]
										: undefined,
								referredBy,
							});
						}),
				);
				setFinancialForms(availableForms);
				setLoading(false);
			})
			.catch((e) => {
				// setLoading(false);
				Swal.fire({
					title: 'Error!',
					text: e ? e.message : 'ozoner no valido',
					icon: 'error',
					confirmButtonText: 'Aceptar',
				}).then(() => {
					navigate('/solicitudes');
				});
				throw e;
			});
	};

	useEffect(() => {
		if (ozoner?.documents) {
			updateSelfieFromDocuments(ozoner.documents);
		}
	}, [ozoner?.documents]);

	useEffect(() => {
		if (formId && financialForms.length) {
			const index = financialForms.findIndex(
				(form) => form.form._id === formId,
			);
			if (index >= 0) {
				setIndexForm(index);
			}
		}
	}, [formId, financialForms]);

	useEffect(() => {
		updatePaymentLinks(false);
		updateZapsignDocuments(false);
	}, [financialForms, indexForm]);

	useEffect(() => {
		initializeContext();
	}, [id]);

	const updatePaymentLinks = (notify = true) => {
		if (financialForms[indexForm]?.form?._id) {
			setPaymentLinksLoading(true);
			getPaymentLinks({
				financialForm: financialForms[indexForm].form._id,
			})
				.then((links) => {
					if (notify) {
						toasterNotification({
							msg: 'Se actualizo el estado de los pagos',
							style: 'light',
							toasterType: toasterOptions.success,
						});
					}
					setPaymentLinksLoading(false);
					setPaymentLinks(links);
					if (notify) initializeContext();
				})
				.catch((e) => {
					commonErrorHandler(e);
					setPaymentLinksLoading(false);
				});
		}
	};
	const updateZapsignDocuments = (notify = true) => {
		const currentForm = financialForms[indexForm]?.form;
		const contractId =
			currentForm?.loanApplication?.zapsignContractId ??
			(currentForm as any)?.zapsignContractId;
		if (contractId) {
			setZapsignDocumentsLoading(true);
			const updatedDocuments = { ...zapsignDocuments };
			const p = [
				getDocument(contractId).then((doc) => {
					updatedDocuments[doc.token] = doc;
				}),
			];
			Promise.all(p)
				.then(() => {
					if (notify) {
						toasterNotification({
							msg: 'Se actualizo los documentos',
							style: 'light',
							toasterType: toasterOptions.success,
						});
					}
					setZapsignDocuments(updatedDocuments);
					setZapsignDocumentsLoading(false);
				})
				.catch((e) => {
					commonErrorHandler(e);
					setZapsignDocumentsLoading(false);
				});
		}
	};

	const updateComment = (comment: string) => {
		if (ozoner)
			updateOzoner(ozoner._id, { comment }).then(() => {
				setOzoner({ ...ozoner, comment });
			});
	};

	const updateFormData = (form: GenericFinancialForm) => {
		const index = financialForms.findIndex(
			(availableForm) => availableForm.form._id === form._id,
		);
		if (index < 0) return;

		const newFinancialForms = [...financialForms];
		newFinancialForms[index] = { ...newFinancialForms[index], form };
		setFinancialForms(newFinancialForms);

		const vehicleInternalId = (form as any).vehicle?.internalId as
			| string
			| undefined;
		if (
			vehicleInternalId &&
			vehicleInternalId !== newFinancialForms[index].vehicle?.internalId
		) {
			getVehicleByInternalId(vehicleInternalId).then((vehicle) => {
				setFinancialForms((prev) => {
					const idx = prev.findIndex((af) => af.form._id === form._id);
					if (idx < 0) return prev;
					const updated = [...prev];
					updated[idx] = { ...updated[idx], vehicle };
					return updated;
				});
			});
		}
	};
	const updateLoanData = (loan: LoanWithExtraFields) => {
		const index = financialForms.findIndex(
			(availableForm) => availableForm.form._id === loan.financialForm._id,
		);
		if (index >= 0) {
			const newFinancialForms = [...financialForms];
			newFinancialForms[index].loan = loan;
			setFinancialForms(newFinancialForms);
		}
	};

	const handleDetailsVehicle = (e: any, internalId: string) => {
		e.stopPropagation();
		navigate(`/inventory/${internalId}`);
	};

	useEffect(() => {
		const user = getUser();
		findAllOzonios(user?.company._id ?? '', '').then((ozoniosListing) => {
			const ozoniosData: { [key: string]: Ozonio } = {};
			setOzoniosList(
				ozoniosListing.data
					.filter((ozonio) => ozonio.isActive)
					.sort((a: any, b: any) => {
						if (a.name < b.name) {
							return -1;
						}
						if (a.name > b.name) {
							return 1;
						}
						return 0;
					})
					.map((ozonio: any) => {
						ozoniosData[ozonio._id] = ozonio;
						return {
							value: ozonio._id,
							label: ozonio.name,
						};
					}),
			);
			setOzonios(ozoniosData);
		});
		findAllOzonios(
			user?.company._id ?? '',
			'',
			undefined,
			undefined,
			Roles.SELLER,
		).then((ozoniosListing) => {
			setSellerOptions(
				ozoniosListing.data
					.filter((ozonio) => ozonio.isActive)
					.sort((a: any, b: any) => {
						if (a.name < b.name) {
							return -1;
						}
						if (a.name > b.name) {
							return 1;
						}
						return 0;
					})
					.map((ozonio: any) => ({
						value: ozonio._id,
						label: ozonio.name,
					})),
			);
		});
		findAllOzonios(
			user?.company._id ?? '',
			'',
			undefined,
			undefined,
			Roles.COLLECTOR_MANAGER,
		).then((ozoniosListing) => {
			setCollectionManagerOptions(
				ozoniosListing.data
					.filter((ozonio) => ozonio.isActive)
					.sort((a: any, b: any) => {
						if (a.name < b.name) {
							return -1;
						}
						if (a.name > b.name) {
							return 1;
						}
						return 0;
					})
					.map((ozonio: any) => ({
						value: ozonio._id,
						label: ozonio.name,
					})),
			);
		});
	}, []);

	const contextValue: OzonerContextModel = useMemo(
		() => ({
			id,
			tab,
			setTab,
			initializeContext,
			setId,
			loading,
			setLoading,
			ozoner,
			setOzoner,
			financialForms,
			loans,
			selfie,
			indexForm,
			formId,
			setFormId,
			financialForm: financialForms?.[indexForm],
			vehicle: financialForms?.[indexForm]?.vehicle,
			// TODO: add muffin and truora validation for CURP
			canValidateCurp: true,
			validCurp: true,
			paymentLinks,
			paymentLinksLoading,
			setPaymentLinksLoading,
			updatePaymentLinks,
			updateComment,
			updateFormData,
			updateLoanData,
			handleDetailsVehicle,
			ozonios,
			ozoniosList,
			sellerOptions,
			collectionManagerOptions,
			openVehicle,
			setOpenVehicle,
			hasLoan,
			zapsignDocuments,
			zapsignDocumentsLoading,
			updateZapsignDocuments,
			referrals,
		}),
		[
			id,
			tab,
			loading,
			ozoner,
			financialForms,
			loans,
			selfie,
			indexForm,
			formId,
			ozonios,
			ozoniosList,
			sellerOptions,
			collectionManagerOptions,
			openVehicle,
			hasLoan,
			paymentLinks,
			paymentLinksLoading,
			zapsignDocuments,
			zapsignDocumentsLoading,
			referrals,
		],
	);
	return (
		<ozonerContext.Provider value={contextValue}>
			{children}
		</ozonerContext.Provider>
	);
};
export {};
